import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ResumePreview } from '@/components/ResumePreview';
import { FileText, Loader2 } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const EMPTY_SKILLS = { technical: [], soft: [], languages: [] };

function LoadingState() {
  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center">
      <Loader2 className="w-8 h-8 animate-spin text-[#FB923C]" />
    </div>
  );
}

function ErrorState({ message }) {
  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center">
      <div className="text-center">
        <FileText className="w-16 h-16 text-slate-300 mx-auto mb-4" />
        <p className="text-lg text-slate-600 mb-4" data-testid="share-error">{message}</p>
        <Link to="/" className="text-[#FB923C] hover:text-orange-600 font-bold">Go to Aarova AI</Link>
      </div>
    </div>
  );
}

function SharedResumeView({ resume }) {
  const rd = resume?.data || {};
  return (
    <div className="min-h-screen bg-slate-100">
      <header className="bg-white border-b border-slate-200 py-3">
        <div className="max-w-4xl mx-auto px-6 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <FileText className="w-6 h-6 text-[#FB923C]" />
            <span className="text-lg font-black text-slate-900" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>Aarova AI</span>
          </div>
          <Link to="/register" className="text-sm font-bold text-[#FB923C] hover:text-orange-600" data-testid="create-yours-link">Create yours free</Link>
        </div>
      </header>
      <main className="max-w-3xl mx-auto px-6 py-8" data-testid="shared-resume">
        <ResumePreview
          template={resume?.template || 'modern'}
          personalInfo={rd.personal_info || {}}
          professionalSummary={rd.professional_summary || ''}
          workExperience={rd.work_experience || []}
          education={rd.education || []}
          skills={rd.skills || EMPTY_SKILLS}
          projects={rd.projects || []}
          certifications={rd.certifications || []}
        />
      </main>
    </div>
  );
}

export default function SharedResumePage() {
  const { shareToken } = useParams();
  const [resume, setResume] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API_URL}/api/share/${shareToken}`);
        if (!cancelled) setResume(data);
      } catch (err) {
        if (!cancelled) setError('Resume not found or link has expired.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [shareToken]);

  if (loading) return <LoadingState />;
  if (error) return <ErrorState message={error} />;
  return <SharedResumeView resume={resume} />;
}
